import React from 'react';
import { OAuth2Client } from '@byteowls/capacitor-oauth2';
import { Browser } from '@capacitor/browser';
import { get, set, remove, _iamKey, getAuthTypePreference } from '../util/Preferences';
import { isPlatform } from '@ionic/react';
import { clearApiOptionsData, clearApiSession, getApiSession, _apiSessionKey } from './ApiOptionsHelper';
import {
    isBiometricEnabled,
    storeBiometricCredential,
    getBiometricCredential,
    deleteBiometricCredential,
} from './Biometric';

// Flip to true once the Azure B2C user flow / app registration is updated to
// return refresh tokens (offline_access). When true, biometric sign-in stores
// the long-lived refresh token and silently mints fresh access tokens on
// relaunch. While false, it stores the short-lived access token and biometric
// unlock only skips the login screen while that token is still valid.
// See BIOMETRIC-SETUP.md.
export const REFRESH_TOKEN_AVAILABLE = false;

const BEARER_TOKEN = "BearerToken";
const BEARER_TOKEN_PARSED = "BearerTokenParsed";
const BEARER_TOKEN_PORTAL = "BearerTokenPortal";
const BEARER_TOKEN_PORTAL_PARSED = "BearerTokenPortalParsed";
const PORTAL_API_TOKEN = "PortalApiToken"; 
const PORTAL_API_TOKEN_PARSED = "PortalApiTokenParsed";
const PORTAL_USER_UNIQUEID = "PortalUserUniqueId";
const B2C_SCOPE_CURRENT = "CurrentB2CScope";
export const ID_TOKEN = "IdToken";

interface AuthContent {
    loggedIn: boolean | undefined;
    setLoggedIn: ((loggedIn: boolean) => void) | undefined,
}

export const AuthContext = React.createContext<AuthContent>({
    loggedIn: false,
    setLoggedIn: undefined
});

export const signin = async () => {
    const oAuthOptions = await getOAuthOptions(false);
    return await OAuth2Client.authenticate(oAuthOptions).then(async response => {
        let accessTokenResponse = response['access_token_response'];
        let jwtToken = accessTokenResponse['access_token'];
        await storeAuthData(jwtToken);
        //store b2c scope used
        await set(B2C_SCOPE_CURRENT, oAuthOptions.scope);
        //persist session token for biometric sign-in if the user opted in
        await persistBiometricCredentialIfEnabled(accessTokenResponse);
        return true;
    }).catch(reason => {
        console.log('B2C SIGN IN CALL ERROR');
        console.log('Auth failed: ' + reason);

        return false;
    });
}

export const signinPortal = async () => {
    const currentUrl = window.location.href;
    // Bug #879938: a launch from the Tenant Portal (flagged with the 'portalLaunch'
    // marker by UserIdentity.checkIAMQueryParam) carries no email/login_hint, so B2C
    // would otherwise silently reuse an existing SSO session and sign the launcher in
    // as the *previous* user. Force a fresh sign-in on that path only, and consume the
    // one-shot marker immediately. A normal app relaunch (no marker) and a password-
    // reset return are left untouched, so returning users are never re-prompted.
    const isPortalLaunch = sessionStorage.getItem('portalLaunch') === 'true';
    if (isPortalLaunch) {
        sessionStorage.removeItem('portalLaunch');
    }
    const isPasswordReset = currentUrl.includes('access_denied') && currentUrl.includes('The+user+has+forgotten+their+password');
    var oauthOptions = await getOAuthOptions(true, isPortalLaunch && !isPasswordReset);
    if (isPasswordReset) {
        let authConfig = await getAuthConfig();
        if (authConfig)
            oauthOptions.authorizationBaseUrl = authConfig.AUTHORIZATION_PASSWORD_RESET_BASE_URL;
    }
    return await OAuth2Client.authenticate(oauthOptions).then(async response => {
        let accessTokenResponse = response['access_token_response'];
        let jwtToken = accessTokenResponse['access_token'];
        let idToken = accessTokenResponse['id_token'];
        await storePortalAuthData(jwtToken, idToken);
        return true;
    }).catch(reason => {
        console.log('(PORTAL) B2C SIGN IN CALL ERROR');
        console.log('(PORTAL) Auth failed: ' + reason);

        return false;
    });
}

export const signout = async (relogin: boolean) => {
    // Remove any biometric-stored session token; the session is ending.
    // The user's biometric opt-in preference is kept, so the next successful
    // login re-enrolls automatically without prompting again.
    await deleteBiometricCredential();
    if (!relogin) {
        //logout from b2c
        const logoutUrl = encodeURI(await getLogoutUrl());
        // Clear session data before navigating away so it is not re-used on next app load
        await clearAuthData();
        await Browser.open({ url: logoutUrl, windowName: "_self" }).then(() => setTimeout(() => {
            Browser.close();
        }, 2000));
        return true;
    }

    return await OAuth2Client.logout(await getOAuthOptions(false)).then(async response => {
        await clearAuthData();
        return true;
    }).catch(reason => {
        console.log('Signout failed: ' + reason);
        return false;
    });
}

export const signoutPortal = async () => {
    return await OAuth2Client.logout(await getOAuthOptions(true)).then(async response => {
        await clearPortalAuthData();
        return true;
    }).catch(reason => {
        console.log('(PORTAL) Signout failed: ' + reason);
        return false;
    });
}

export const getBearerToken = async () => {
    return await get(BEARER_TOKEN).then(result => {
        if (!!!result) {
            console.log('Bearer token not found');
        }
        return result;
    });
}

export const getBearerTokenParsed = async () => {
    return await get(BEARER_TOKEN_PARSED).then(result => {
        if (!!!result) {
            console.log('Bearer token not found');
        }
        return result;
    });
}

export const getPortalBearerToken = async () => {
    return await get(BEARER_TOKEN_PORTAL).then(result => {
        if (!!!result) {
            console.log('Portal bearer token not found');
        }
        return result;
    });
}

export const getUserName = async (): Promise<string> => {
    return await get(BEARER_TOKEN_PARSED).then(token => {
        if (!!token) {
            let jwtParsed = JSON.parse(JSON.stringify(token));
            return jwtParsed.name;
        }
        return null;
    });
};

/**
 * After a successful B2C sign-in, store the session token in the device
 * secure store so it can be unlocked with biometrics on the next launch.
 * No-op unless the user has enabled biometric sign-in.
 *
 * While REFRESH_TOKEN_AVAILABLE is false we store the (short-lived) access
 * token; once B2C returns refresh tokens we store the refresh token instead.
 */
const persistBiometricCredentialIfEnabled = async (accessTokenResponse: any) => {
    try {
        if (!(await isBiometricEnabled())) {
            return;
        }
        const refreshToken = accessTokenResponse['refresh_token'];
        const accessToken = accessTokenResponse['access_token'];
        const tokenToStore = (REFRESH_TOKEN_AVAILABLE && !!refreshToken)
            ? refreshToken
            : accessToken;
        if (!tokenToStore) {
            return;
        }
        const username = (await getUserName()) || 'eam-user';
        await storeBiometricCredential(tokenToStore, username);
    } catch (error) {
        console.log('persistBiometricCredentialIfEnabled error:', error);
    }
};

/**
 * Whether a session can actually be restored from the biometric-stored
 * credential - used to decide whether prompting for biometrics is worthwhile.
 *
 * - REFRESH_TOKEN_AVAILABLE true: any stored refresh token is worth trying
 *   (the token endpoint is the real validity check).
 * - REFRESH_TOKEN_AVAILABLE false: the stored value is an access token, so it
 *   is only useful while unexpired - otherwise we skip the prompt and go
 *   straight to B2C rather than making the user authenticate twice.
 */
export const canRestoreBiometricSession = async (): Promise<boolean> => {
    const stored = await getBiometricCredential();
    if (!stored) {
        return false;
    }
    if (REFRESH_TOKEN_AVAILABLE) {
        return true;
    }
    try {
        const parsed = parseJwt(stored);
        return !!parsed && (new Date().valueOf() < parsed.exp * 1000);
    } catch (error) {
        console.log('canRestoreBiometricSession parse error:', error);
        return false;
    }
};

/**
 * Restores an EAM session from the biometric-stored credential.
 * Call only after the biometric prompt has succeeded.
 *
 * - REFRESH_TOKEN_AVAILABLE true: exchanges the stored refresh token for a
 *   fresh access token (true silent re-login, works long after app close).
 * - REFRESH_TOKEN_AVAILABLE false: restores the stored access token and
 *   reports whether it is still valid (interim behaviour).
 *
 * Returns true when a valid session is in place, false to fall back to B2C login.
 */
export const restoreSessionFromBiometric = async (): Promise<boolean> => {
    try {
        const stored = await getBiometricCredential();
        if (!stored) {
            return false;
        }
        if (REFRESH_TOKEN_AVAILABLE) {
            return await refreshAccessTokenFromBiometric(stored);
        }
        // Interim mode: the stored value is an access token.
        await storeAuthData(stored);
        await set(B2C_SCOPE_CURRENT, await getScope(false));
        return await validateBearerToken();
    } catch (error) {
        console.log('restoreSessionFromBiometric error:', error);
        return false;
    }
};

/**
 * Resolves the B2C token endpoint for the current platform. The mobile code
 * exchange uses the platform-specific endpoint (see getOAuthOptions), so the
 * refresh must hit the same one - that guarantees the refresh token is redeemed
 * at the same tenant that issued it. The top-level ACCESS_TOKEN_ENDPOINT is a
 * last-resort fallback (it is empty in the current config).
 *
 * NOTE: the ANDROID and IOS endpoints in authConfig.json currently point at
 * different B2C tenants (apteanid vs eamb2cdev). That is a pre-existing config
 * inconsistency - confirm both are correct for each environment before relying
 * on full mode in production. This helper intentionally does not "correct" it;
 * it mirrors whatever the initial code exchange used.
 */
const getAccessTokenEndpoint = (authConfig: any): string => {
    if (isPlatform('android') && !!authConfig.ANDROID?.ACCESS_TOKEN_ENDPOINT) {
        return authConfig.ANDROID.ACCESS_TOKEN_ENDPOINT;
    }
    if (isPlatform('ios') && !!authConfig.IOS?.ACCESS_TOKEN_ENDPOINT) {
        return authConfig.IOS.ACCESS_TOKEN_ENDPOINT;
    }
    if (!!authConfig.WEB?.ACCESS_TOKEN_ENDPOINT) {
        return authConfig.WEB.ACCESS_TOKEN_ENDPOINT;
    }
    return authConfig.ACCESS_TOKEN_ENDPOINT || '';
};

/**
 * Exchanges a stored refresh token for a fresh access token against B2C.
 * Only reachable when REFRESH_TOKEN_AVAILABLE is true.
 */
const refreshAccessTokenFromBiometric = async (refreshToken: string): Promise<boolean> => {
    try {
        const authConfig = await getAuthConfig();
        if (!authConfig) {
            return false;
        }
        const tokenEndpoint = getAccessTokenEndpoint(authConfig);
        if (!tokenEndpoint) {
            // Misconfiguration, not a bad token - keep the credential and fall back to B2C login.
            console.log('refreshAccessTokenFromBiometric: no token endpoint configured for this platform');
            return false;
        }
        const scope = await getScope(false);
        const body = new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: authConfig.CLIENT_ID,
            refresh_token: refreshToken,
            scope: scope,
        });
        const response = await fetch(tokenEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        });
        if (!response.ok) {
            console.log('refreshAccessTokenFromBiometric: token endpoint returned', response.status);
            // Stored refresh token is no longer usable; drop it so we stop offering biometric with a dead token.
            await deleteBiometricCredential();
            return false;
        }
        const data = await response.json();
        if (!data.access_token) {
            // Auth succeeded but no access token for the requested resource scope -
            // usually a scope/registration issue, not a bad refresh token. Keep the
            // credential and fall back to B2C rather than silently logging in tokenless.
            console.log('refreshAccessTokenFromBiometric: no access_token in refresh response (check B2CUserFlowScope)');
            return false;
        }
        await storeAuthData(data.access_token);
        await set(B2C_SCOPE_CURRENT, scope);
        // B2C rotates refresh tokens; persist the new one for next time.
        if (data.refresh_token) {
            const username = (await getUserName()) || 'eam-user';
            await storeBiometricCredential(data.refresh_token, username);
        }
        return true;
    } catch (error) {
        console.log('refreshAccessTokenFromBiometric error:', error);
        return false;
    }
};

/**
 * Retrieves the authentication configuration.
 * @returns {Promise<any>} A promise that resolves to the authentication configuration object.
 */
export const getAuthConfig = async () => {
    try {
        const response = await fetch('/authConfig.json');
        if (!response.ok) {
            console.log('Failed to load auth configuration');
        }
        let iam = await getAuthTypePreference(_iamKey),
            authConfigs = await response.json(),
            authConfig = authConfigs.find((x: any) => x.IAM === iam?.value);
        return authConfig;
    } catch (error) {
        console.log('Error fetching auth config:', error);
        return null;
    }
}

export const validateBearerToken = async () => {
    try {
        const token = await getBearerToken();
        if (!!!token) {
            return false;
        }
        //check if aud is valid for the current api scope
        if (await isB2CRefreshRequired()) {
            return false;
        }
        const tokenParsed = await get(BEARER_TOKEN_PARSED);
        if (!!tokenParsed) {
            let today = new Date();
            return (today.valueOf() < tokenParsed.exp * 1000);
        }
        return false;
    } catch (error) {
        console.log('ERROR (validateUserToken): ', error);
        return false;
    }
};

const isB2CRefreshRequired = async () => {
    const apiSession = await getApiSession();
    const currentScope = await get(B2C_SCOPE_CURRENT);
    if (!!apiSession && !!apiSession.B2CUserFlowScope && !!currentScope) {
        // Full mode only: offline_access is appended to obtain a refresh token for
        // biometric silent re-login; it is not part of the API resource scope
        // identity, so ignore it here. In interim mode this uses the exact original
        // comparison - no behaviour change.
        if (REFRESH_TOKEN_AVAILABLE) {
            const normalizeScope = (scope: string) =>
                scope.replace(/offline_access/g, '').trim().replace(/\s+/g, ' ');
            return normalizeScope(currentScope) !== normalizeScope(apiSession.B2CUserFlowScope);
        }
        return currentScope !== apiSession.B2CUserFlowScope;
    }
    return false;
};

export const handlePWACallback = async (url: string, isPortal: boolean) => {
    const urlParams = JSON.stringify(getUrlParams(url));
    const pObj = JSON.parse(urlParams);
    const accessToken = pObj.access_token;
    if (accessToken !== undefined) {
        //store the token
        if (isPortal) {
            await storePortalAuthData(accessToken);
        } else {
            await storeAuthData(accessToken);
            //store b2c scope used
            getOAuthOptions(false).then((oAuthOptions) => {
                set(B2C_SCOPE_CURRENT, oAuthOptions.scope);
            });
        }
        return true;
    } else {
        console.log('Oauth Parameters where not present in return url');

        return false;
    }
};

const getOAuthOptions = async (isPortal: boolean, forceReauth: boolean = false) => {
    let authConfig = await getAuthConfig(),
        loginHint = getLoginHint(isPortal),
        domainHint = getDomainHint(isPortal),
        additionalParameters = {};
    if (loginHint !== null) {
        additionalParameters = {
            "login_hint": loginHint,
            "domain_hint": domainHint
        };
    } else if (forceReauth) {
        // Bug #879938: a portal-initiated launch supplies IAM but not the user's
        // email, so there is no login_hint. Without it, B2C silently reuses any
        // existing SSO session and signs the new launcher in as the *previous*
        // user. Force a fresh sign-in so the launcher's identity is re-established.
        // Only reached on the portal launch path (see signinPortal) and only when
        // there is no login_hint — EAM sign-in, sign-out, and hint-bearing manual
        // logins (PWA and native) are unaffected.
        additionalParameters = {
            "prompt": "login"
        };
    }
    if (!authConfig) return {};
    return {
        appId: authConfig.CLIENT_ID,
        authorizationBaseUrl: authConfig.AUTHORIZATION_BASE_URL,
        accessTokenEndpoint: authConfig.ACCESS_TOKEN_ENDPOINT,
        scope: await getScope(isPortal),
        logsEnabled: authConfig.LOGS_ENABLED,
        web: {
            pkceEnabled: authConfig.WEB.PKCE_ENABLED,
            responseType: authConfig.WEB.RESPONSE_TYPE,
            accessTokenEndpoint: authConfig.WEB.ACCESS_TOKEN_ENDPOINT,
            redirectUrl: (isPortal) ? authConfig.WEB.REDIRECT_URL_PORTAL : authConfig.WEB.REDIRECT_URL,
            windowTarget: authConfig.WEB.WINDOW_TARGET,
            additionalParameters: additionalParameters
        },
        android: {
            pkceEnabled: authConfig.ANDROID.PKCE_ENABLED,
            responseType: authConfig.ANDROID.RESPONSE_TYPE,
            redirectUrl: authConfig.ANDROID.REDIRECT_URL,
            accessTokenEndpoint: authConfig.ANDROID.ACCESS_TOKEN_ENDPOINT,
            handleResultOnNewIntent: authConfig.ANDROID.HANDLE_RESULT_ON_NEWINTENT,
            handleResultOnActivityResult: authConfig.ANDROID.HANDLE_RESULT_ON_ACTIVITYRESULT,
            additionalParameters: additionalParameters
        },
        ios: {
            pkceEnabled: authConfig.IOS.PKCE_ENABLED,
            responseType: authConfig.IOS.RESPONSE_TYPE,
            redirectUrl: authConfig.IOS.REDIRECT_URL,
            accessTokenEndpoint: authConfig.IOS.ACCESS_TOKEN_ENDPOINT,
            additionalParameters: additionalParameters
        }
    }
};

const getLoginHint = (isPortal: boolean) => {
    if (!isPortal) return null;
    let url = new URL(window.location.href),
        searchParams = new URLSearchParams(url.search),
        emailParam = searchParams.get('email');

    return emailParam;
}

const getDomainHint = (isPortal: boolean) => {
    if (!isPortal) return null;
    let url = new URL(window.location.href),
        searchParams = new URLSearchParams(url.search),
        domainHintParam = searchParams.get('domainhint');

    return domainHintParam;
}

const getScope = async (isPortal: boolean) => {
    if (isPortal) {
        let authConfig = await getAuthConfig();
        if (!authConfig) return '';
        return authConfig.SCOPE_PORTAL;
    } else {
        const apiSession = await getApiSession();
        let scope = apiSession.B2CUserFlowScope;
        // Request a refresh token for biometric silent re-login once B2C supports it.
        // Native only - biometric sign-in doesn't exist on web/PWA, so the PWA's
        // auth request is never modified, even in full mode.
        if (REFRESH_TOKEN_AVAILABLE && isPlatform('hybrid') && !!scope && !scope.includes('offline_access')) {
            scope = `${scope} offline_access`;
        }
        return scope;
    }
};

const storeAuthData = async (token: string) => {
    await clearAuthData();
    await set(BEARER_TOKEN, token);
    await set(BEARER_TOKEN_PARSED, parseJwt(token));
}

const storePortalAuthData = async (token: string, idToken: string = '') => {
    await clearPortalAuthData();
    await set(BEARER_TOKEN_PORTAL, token);
    await set(BEARER_TOKEN_PORTAL_PARSED, parseJwt(token));
    await set(ID_TOKEN, idToken);
    await storePortalApiToken(token);
    //reset mobilebootstrap data and apisession for new user
    await updatePortalUserData();
}

export const clearAuthDataAndIamPreference = async () => {
    await clearAuthData();
    await remove(_iamKey);
}

export const clearAuthData = async () => {
    await remove(BEARER_TOKEN);
    await remove(BEARER_TOKEN_PARSED);
    await remove(B2C_SCOPE_CURRENT);
}

const clearPortalAuthData = async () => {
    await remove(BEARER_TOKEN_PORTAL);
    await remove(BEARER_TOKEN_PORTAL_PARSED);
}

export const getPortalApiToken = async () => {
    return await get(PORTAL_API_TOKEN).then(result => {
        if (!!!result) {
            console.log('Portal API token not found');
        }
        return result;
    });
}

export const getPortalApiTokenParsed = async () => {
    return await get(PORTAL_API_TOKEN_PARSED).then(result => {
        if (!!!result) {
            console.log('Portal API token parsed not found');
        }
        return result;
    });
}

const storePortalApiToken = async (token: string) => {
    await clearPortalApiToken();
    await set(PORTAL_API_TOKEN, token);
    await set(PORTAL_API_TOKEN_PARSED, parseJwt(token));
    console.log('Portal API token stored for persistent use');
}

const clearPortalApiToken = async () => {
    await remove(PORTAL_API_TOKEN);
    await remove(PORTAL_API_TOKEN_PARSED);
    console.log('Portal API token cleared');
}

const updatePortalUserData = async () => {
    const jwtPortalParsed = await get(BEARER_TOKEN_PORTAL_PARSED);
    const portalUserUniqueId: string = await get(PORTAL_USER_UNIQUEID);
    if (jwtPortalParsed.sub !== portalUserUniqueId) {
        //clear bootstrap data and api session data
        await clearApiSession();
        await clearApiOptionsData();
        //update user uniqueid
        await remove(PORTAL_USER_UNIQUEID);
        await set(PORTAL_USER_UNIQUEID, jwtPortalParsed.sub);
    }
};

const getLogoutUrl = async () => {
    let authConfig = await getAuthConfig();
    let logoutUrl = globalThis.customLogOutUrl;
    if (!authConfig) return '';
    if (isPlatform('hybrid')) {
        let idToken = await get(ID_TOKEN);
        if (isPlatform('android')) {
            authConfig.ANDROID.POST_LOGOUT_REDIRECT_URL = logoutUrl || authConfig.ANDROID.POST_LOGOUT_REDIRECT_URL;
            return `${authConfig.LOGOUT_URL}?client_id=${authConfig.CLIENT_ID}&response_type=${authConfig.ANDROID.RESPONSE_TYPE}&post_logout_redirect_uri=${authConfig.ANDROID.POST_LOGOUT_REDIRECT_URL}&scope=${authConfig.SCOPE}&id_token_hint=${idToken}`;
        } else if (isPlatform('ios')) {
            authConfig.IOS.POST_LOGOUT_REDIRECT_URL = logoutUrl || authConfig.IOS.POST_LOGOUT_REDIRECT_URL;
            return `${authConfig.LOGOUT_URL}?client_id=${authConfig.CLIENT_ID}&response_type=${authConfig.IOS.RESPONSE_TYPE}&post_logout_redirect_uri=${authConfig.IOS.POST_LOGOUT_REDIRECT_URL}&scope=${authConfig.SCOPE}&id_token_hint=${idToken}`;
        }
    }
    authConfig.WEB.POST_LOGOUT_REDIRECT_URL = logoutUrl || authConfig.WEB.POST_LOGOUT_REDIRECT_URL;
    return `${authConfig.LOGOUT_URL}?client_id=${authConfig.CLIENT_ID}&response_type=${authConfig.WEB.RESPONSE_TYPE}&post_logout_redirect_uri=${authConfig.WEB.POST_LOGOUT_REDIRECT_URL}&scope=${authConfig.SCOPE}`;
}

const parseJwt = (token: string) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

const getUrlParams = (url: string) => {
    const urlString = `${url}`.trim();
    if (urlString.length === 0) {
        return;
    }
    // #132
    let hashIndex = urlString.indexOf("#");
    let queryIndex = urlString.indexOf("?");
    if (hashIndex === -1 && queryIndex === -1) {
        return;
    }
    let paramsIndex;
    if (hashIndex > -1 && queryIndex === -1) {
        paramsIndex = hashIndex;
    }
    else if (queryIndex > -1 && hashIndex === -1) {
        paramsIndex = queryIndex;
    }
    else {
        paramsIndex = hashIndex > -1 && hashIndex < queryIndex ? hashIndex : queryIndex;
    }
    if (urlString.length <= paramsIndex + 1) {
        return;
    }
    const urlParamStr = urlString.slice(paramsIndex + 1);
    const keyValuePairs = urlParamStr.split(`&`);
    // @ts-ignore
    return keyValuePairs.reduce((accumulator, currentValue) => {
        const [key, val] = currentValue.split(`=`);
        if (key && key.length > 0) {
            return Object.assign(Object.assign({}, accumulator), { [key]: decodeURIComponent(val) });
        }
    }, {});
}